/****
 * Custom hook for managing user accessibility preferences.
 *
 * Loads accessibility settings from the backend, applies them to the
 * application at runtime, and allows users to save their preferences.
 * Handles features such as text-to-speech, font scaling, cursor size,
 * high contrast mode, keyboard navigation, focus indicators, and
 * dyslexia-friendly settings to provide a personalized accessible experience.
 */
import * as React from "react";

import { useAuth } from "@/hooks/useAuth";
import type { AccessibilitySettings } from "@/lib/accessibility";
import { applySettingsToCSS, type CursorSize, DEFAULTS, enableTTS } from "@/lib/accessibility";
import {
  type AccessibilitySettingsDTO,
  getAccessibilitySettings,
  saveAccessibilitySettings,
} from "@/services/accessibility";

import { useDeviceType } from "./useDeviceType";
import { useMsal } from "@azure/msal-react";

/**
 * Safely normalize backend cursor size
 */
function normalizeCursorSize(value: string): CursorSize {
  if (value === "large" || value === "xlarge") {
    return value;
  }
  return "normal";
}

/**
 * Clamp helper
 */
function clampRate(rate: number | undefined): number {
  return Math.max(0.5, Math.min(2.0, rate ?? 1.0));
}

/**
 * Map backend DTO → UI model
 */
function dtoToSettings(
  dto: AccessibilitySettingsDTO,
  isTouchDevice: boolean
): AccessibilitySettings {
  const ttsEnabled = !isTouchDevice ? (dto.tts ?? false) : false;

  return {
    ...DEFAULTS,

    fontScale: (dto.zoom_level ?? 100) / 100,

    highContrast: dto.high_contrast ?? false,
    underlineLinks: dto.underline_links ?? false,
    increasedTextSpacing: dto.text_spacing ?? false,
    reduceMotion: dto.reduce_motion ?? false,
    enhancedKeyboardNav: dto.keyboard_navigation ?? false,
    enhancedFocusIndicators: dto.focus_indicators ?? false,

    cursorSize: normalizeCursorSize(dto.cursor_size),

    ttsEnabled,
    speechRate: clampRate(dto.speech_rate),

    ttsOnFocus: dto.tts_on_focus ?? false,
    ttsOnHover: dto.tts_on_hover ?? false,

    screenReaderOptimization: dto.screen_reader_optimization ?? false,

    dyslexiaFriendlyFont: dto.dyslexia_friendly_font ?? false,
  };
}

/**
 * Map UI model → backend DTO
 */
function settingsToDto(
  userId: string,
  displayName: string | undefined,
  emailId: string ,
  settings: AccessibilitySettings
): AccessibilitySettingsDTO {
  return {
    user_id: userId,
    username: displayName,
    email_id: emailId,

    zoom_level: Math.round(settings.fontScale * 100),

    high_contrast: settings.highContrast,
    underline_links: settings.underlineLinks,
    text_spacing: settings.increasedTextSpacing,
    reduce_motion: settings.reduceMotion,

    keyboard_navigation: settings.enhancedKeyboardNav,
    focus_indicators: settings.enhancedFocusIndicators,

    cursor_size: settings.cursorSize,

    tts: settings.ttsEnabled,
    speech_rate: clampRate(settings.speechRate),

    tts_on_focus: settings.ttsOnFocus,
    tts_on_hover: settings.ttsOnHover,

    screen_reader_optimization: settings.screenReaderOptimization,

    dyslexia_friendly_font: settings.dyslexiaFriendlyFont,
  };
}

export function useAccessibilitySettings() {
  const { account } = useAuth();
  const { accounts } = useMsal();
  const { isMobile, isTablet } = useDeviceType();

  const userId = account?.id;
  const isTouchDevice = isMobile || isTablet;

  const [settings, setSettings] = React.useState<AccessibilitySettings>(DEFAULTS);
  const userPrincipalName = accounts[0]?.username || account?.userPrincipalName;

  /**
   * Load settings from backend
   */
  const loadSettings = React.useCallback(async () => {
    if (!userId) return;

    try {
      const data = await getAccessibilitySettings(userId);

      if (data) {
        setSettings(dtoToSettings(data, isTouchDevice));
      }
    } catch (error) {
      console.error("Error loading accessibility settings:", error);
    }
  }, [userId, isTouchDevice]);

  React.useEffect(() => {
    if (userId) {
      loadSettings();
    }
  }, [userId, loadSettings]);

  /**
   * Save settings to backend
   */
  const saveSettings = React.useCallback(
    async (override?: AccessibilitySettings) => {
      if (!userId) return;

      const data = override ?? settings;

      try {
        await saveAccessibilitySettings(
          settingsToDto(userId, account?.displayName || account?.username, userPrincipalName, data)
        );
      } catch (error) {
        console.error("Error saving accessibility settings:", error);
      }
    },
    [userId, account?.displayName, account?.username, userPrincipalName, settings]
  );

  /**
   * Apply runtime accessibility behavior
   */
  React.useEffect(() => {
    applySettingsToCSS(settings);
    enableTTS(settings);
  }, [settings]);

  React.useEffect(() => {
    const root = document.documentElement;

    root.classList.toggle("a11y-dyslexia", settings.dyslexiaFriendlyFont);

    root.classList.toggle("a11y-high-contrast", settings.highContrast);
  }, [settings.dyslexiaFriendlyFont, settings.highContrast]);

  return {
    settings,
    setSettings,
    loadSettings,
    saveSettings,
  };
}
